import { useState, useCallback } from 'react';
import { api } from '../api';
import { normalizeApiOutcomes, buildAutocompleteOutcomes } from '../helpers';
import { getDidYouMeanSuggestions } from '../didYouMean';

/**
 * Manages the question classification → vague refinement → market generation pipeline.
 * Keeps all API orchestration out of the UI component.
 */
export const useQuestionFlow = ({ updateMarketData, setCurrentStep }) => {
  const [isClassifying, setIsClassifying] = useState(false);
  const [vagueStage, setVagueStage] = useState('none');
  const [pendingVagueQuestion, setPendingVagueQuestion] = useState('');
  const [clarifications, setClarifications] = useState([]);
  const [currentClarifyQuestion, setCurrentClarifyQuestion] = useState('');
  const [refinedQuestion, setRefinedQuestion] = useState('');
  const [pendingCorrectionType, setPendingCorrectionType] = useState(null);
  const [pendingResolutionMethod, setPendingResolutionMethod] = useState(null);

  // ─── Generate market + image from a clear question ──
  const proceedToGenerateAndLiquidity = useCallback(
    async (question, resMethod) => {
      setIsClassifying(true);
      try {
        const marketRes = await api.generateMarket(question, resMethod);
        const market = marketRes?.data || marketRes;
        const normalizedOutcomes = normalizeApiOutcomes(market.outcomes || []);
        const marketCategories = market.categories || [];

        updateMarketData({
          question,
          description: market.description,
          category: marketCategories[0]?.toLowerCase() || 'other',
          outcomes: normalizedOutcomes,
          isPublic: true,
          resolutionDate: market.resolutionDate
            ? new Date(market.resolutionDate)
            : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        });

        return { success: true };
      } catch {
        return { success: false };
      } finally {
        setIsClassifying(false);
      }
    },
    [updateMarketData]
  );

  // ─── Classify and route a question ──────────────────
  const classifyAndRoute = useCallback(
    async (question) => {
      // Client-side spell/phrasing check (instant, before API call)
      const localSuggestion = getDidYouMeanSuggestions(question);
      if (localSuggestion && localSuggestion.suggestions.length > 0) {
        const corrected = localSuggestion.suggestions[0];
        setPendingVagueQuestion(question);
        setRefinedQuestion(corrected);
        setPendingCorrectionType(localSuggestion.type === 'spelling' ? 'spelling' : 'correction');
        setVagueStage('suggest-refined');
        return { type: localSuggestion.type === 'spelling' ? 'spelling' : 'semantic' };
      }

      setIsClassifying(true);
      try {
        const classification = await api.classifyQuestion(question);
        setIsClassifying(false);
        setPendingResolutionMethod(classification.resolutionMethod);

        if (classification.classification === 'restricted') {
          return { type: 'restricted' };
        }

        if (classification.classification === 'spelling') {
          const corrected =
            classification.correctedQuestion ||
            (classification.corrections && classification.corrections[0]) ||
            question;
          setPendingVagueQuestion(question);
          setRefinedQuestion(corrected);
          setPendingCorrectionType('spelling');
          setVagueStage('suggest-refined');
          return { type: 'spelling' };
        }

        if (classification.classification === 'semantic') {
          const corrected =
            classification.correctedQuestion ||
            (classification.corrections && classification.corrections[0]) ||
            question;
          setPendingVagueQuestion(question);
          setRefinedQuestion(corrected);
          setPendingCorrectionType('correction');
          setVagueStage('suggest-refined');
          return { type: 'semantic' };
        }

        if (
          classification.classification === 'non-specific' ||
          classification.classification === 'unresolvable'
        ) {
          setPendingVagueQuestion(question);
          setClarifications([]);
          setVagueStage('asking-details');

          const refineResult = await api.refineQuestion({
            originalQuestion: question,
            clarifications: [],
          });

          if (refineResult.stage === 'needs-clarification' && refineResult.clarifyingQuestion) {
            setCurrentClarifyQuestion(refineResult.clarifyingQuestion);
            return { type: 'needs-clarification', clarifyingQuestion: refineResult.clarifyingQuestion };
          }
          if (refineResult.stage === 'refined' && refineResult.refinedQuestion) {
            setRefinedQuestion(refineResult.refinedQuestion);
            setVagueStage('suggest-refined');
            return { type: 'refined-from-vague' };
          }
          return { type: 'needs-clarification' };
        }

        // Clear question — proceed directly
        return { type: 'clear', resolutionMethod: classification.resolutionMethod };
      } catch {
        setIsClassifying(false);
        return { type: 'error' };
      }
    },
    []
  );

  // ─── Handle autocomplete-selected question ──────────
  const handleAutocompleteQuestion = useCallback(
    (question, autocompleteData) => {
      const outcomes = buildAutocompleteOutcomes(autocompleteData.options);
      const category =
        (autocompleteData.categories?.[0]?.toLowerCase()) || 'other';
      const description = autocompleteData.rules || '';
      const acCategories = autocompleteData.categories || [];

      updateMarketData({
        question,
        description,
        category,
        outcomes,
        isPublic: true,
        resolutionDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      });

    },
    [updateMarketData]
  );

  // ─── Handle clarification answer ────────────────────
  const handleClarificationAnswer = useCallback(
    async (answer) => {
      const newClarifications = [...clarifications, { question: currentClarifyQuestion, answer }];
      setClarifications(newClarifications);
      setIsClassifying(true);

      try {
        const refineResult = await api.refineQuestion({
          originalQuestion: pendingVagueQuestion,
          clarifications: newClarifications,
        });

        if (refineResult.stage === 'needs-clarification' && refineResult.clarifyingQuestion) {
          setCurrentClarifyQuestion(refineResult.clarifyingQuestion);
          return { type: 'needs-clarification', clarifyingQuestion: refineResult.clarifyingQuestion };
        }
        if (refineResult.stage === 'refined' && refineResult.refinedQuestion) {
          setRefinedQuestion(refineResult.refinedQuestion);
          setVagueStage('suggest-refined');
          return { type: 'refined' };
        }
        return { type: 'error' };
      } catch {
        return { type: 'error' };
      } finally {
        setIsClassifying(false);
      }
    },
    [clarifications, currentClarifyQuestion, pendingVagueQuestion]
  );

  // ─── Accept a refined question ──────────────────────
  const handleAcceptRefined = useCallback(
    async (editedQuestion) => {
      const finalQuestion = editedQuestion || refinedQuestion;

      if (pendingCorrectionType) {
        setPendingCorrectionType(null);
        setVagueStage('none');

        // Re-classify the corrected question
        setIsClassifying(true);
        try {
          const reClassify = await api.classifyQuestion(finalQuestion);
          setPendingResolutionMethod(reClassify.resolutionMethod);

          if (
            reClassify.classification === 'non-specific' ||
            reClassify.classification === 'unresolvable'
          ) {
            setPendingVagueQuestion(finalQuestion);
            setClarifications([]);
            setVagueStage('asking-details');
            const refineResult = await api.refineQuestion({
              originalQuestion: finalQuestion,
              clarifications: [],
            });
            setIsClassifying(false);

            if (refineResult.stage === 'needs-clarification' && refineResult.clarifyingQuestion) {
              setCurrentClarifyQuestion(refineResult.clarifyingQuestion);
              return {
                type: 'needs-clarification',
                clarifyingQuestion: refineResult.clarifyingQuestion,
                question: finalQuestion,
              };
            }
            if (refineResult.stage === 'refined' && refineResult.refinedQuestion) {
              setRefinedQuestion(refineResult.refinedQuestion);
              setVagueStage('suggest-refined');
              return { type: 'refined-from-vague', question: finalQuestion };
            }
            return { type: 'needs-clarification', question: finalQuestion };
          }

          setIsClassifying(false);
          setPendingVagueQuestion('');
          setClarifications([]);
          return {
            type: 'clear',
            question: finalQuestion,
            resolutionMethod: reClassify.resolutionMethod,
          };
        } catch {
          setIsClassifying(false);
          setPendingVagueQuestion('');
          return { type: 'error', question: finalQuestion };
        }
      }

      // Non-correction accept
      setVagueStage('none');
      setPendingVagueQuestion('');
      setClarifications([]);
      return { type: 'accepted', question: finalQuestion, resolutionMethod: pendingResolutionMethod };
    },
    [refinedQuestion, pendingCorrectionType, pendingResolutionMethod]
  );

  // ─── Re-classify after edit from rephrase ───────────
  const reclassifyForRephrase = useCallback(async (newQuestion) => {
    setIsClassifying(true);
    try {
      const reClassify = await api.classifyQuestion(newQuestion);
      setIsClassifying(false);
      if (reClassify.classification === 'clear') {
        return { type: 'clear', resolutionMethod: reClassify.resolutionMethod };
      }
      return { type: 'not-clear' };
    } catch {
      setIsClassifying(false);
      return { type: 'not-clear' };
    }
  }, []);

  // ─── Convenience resets ─────────────────────────────
  const resetQuestionFlow = useCallback(() => {
    setVagueStage('none');
    setPendingVagueQuestion('');
    setClarifications([]);
    setRefinedQuestion('');
    setPendingCorrectionType(null);
    setPendingResolutionMethod(null);
  }, []);

  const startManualFlow = useCallback(() => {
    setVagueStage('explain-manual');
  }, []);

  const startRephraseFlow = useCallback(() => {
    setPendingCorrectionType(null);
    setVagueStage('rephrasing');
  }, []);

  const confirmManualAndProceed = useCallback(() => {
    setVagueStage('none');
    const question = pendingVagueQuestion;
    setPendingVagueQuestion('');
    return question;
  }, [pendingVagueQuestion]);

  return {
    // State
    isClassifying,
    vagueStage,
    setVagueStage,
    pendingVagueQuestion,
    setPendingVagueQuestion,
    clarifications,
    currentClarifyQuestion,
    refinedQuestion,
    setRefinedQuestion,
    pendingCorrectionType,
    setPendingCorrectionType,
    pendingResolutionMethod,

    // Actions
    classifyAndRoute,
    proceedToGenerateAndLiquidity,
    handleAutocompleteQuestion,
    handleClarificationAnswer,
    handleAcceptRefined,
    reclassifyForRephrase,
    resetQuestionFlow,
    startManualFlow,
    startRephraseFlow,
    confirmManualAndProceed,
  };
};
